import { FC, Fragment } from "preact/compat";
import { useCallback } from "preact/hooks";
import { Transition, Dialog as _HeadlessDialog } from "@headlessui/react";
import { useBreakpoint, useDialogUrlState } from "~/hooks";
import type { DialogProps } from "./interfaces";
import cn from "classnames";

// there's an issue with headlessui types
// TS doesn't recognize className prop for Dialog component
//
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HeadlessDialog = _HeadlessDialog as any;

const Dialog: FC<DialogProps> = ({
    show,
    onClose,
    children,
    zIndex = 50,
    centered = true,
    containerProps,
    noUrlState,
    beforeEnter,
    afterEnter,
    beforeLeave,
    afterLeave,
}) => {
    const bpMd = useBreakpoint("md");
    const defHandleClose = useCallback(() => (show.value = false), [show]);

    useDialogUrlState(show, { skip: noUrlState });

    const handleClose = onClose ?? defHandleClose;

    return (
        <Transition appear show={show.value}>
            <HeadlessDialog
                as="div"
                style={{ zIndex }}
                className="relative"
                onClose={handleClose}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    beforeEnter={beforeEnter}
                    afterEnter={afterEnter}
                    beforeLeave={beforeLeave}
                    afterLeave={afterLeave}
                >
                    <div
                        aria-hidden="true"
                        className="fixed bottom-0 left-0 right-0 top-0 bg-white/50 backdrop-blur md:left-[50px] md:top-[50px] md:bg-gray-500/30"
                    />
                </Transition.Child>
                <div className="fixed bottom-0 left-0 right-0 top-0 max-h-screen overflow-y-hidden md:left-[50px] md:top-[50px]">
                    <div
                        {...containerProps}
                        className={cn(
                            "flex min-h-full justify-center p-0 lg:p-4",
                            centered
                                ? "items-center"
                                : "items-start pt-[calc(54px+var(--safe-top))]",
                        )}
                    >
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom={
                                bpMd.value
                                    ? "opacity-0 translate-y-6"
                                    : "opacity-0 scale-105"
                            }
                            enterTo={
                                bpMd.value
                                    ? "opacity-100 translate-y-0"
                                    : "opacity-100 scale-100"
                            }
                            leave="ease-in duration-200"
                            leaveFrom={
                                bpMd.value
                                    ? "opacity-100 translate-y-0"
                                    : "opacity-100 scale-100"
                            }
                            leaveTo={
                                bpMd.value
                                    ? "opacity-0 translate-y-6"
                                    : "opacity-0 scale-105"
                            }
                        >
                            <HeadlessDialog.Panel className="flex">
                                {children}
                            </HeadlessDialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </HeadlessDialog>
        </Transition>
    );
};

export default Dialog;
